import {
  $UserAddPermissions,
  ACTIONS,
  UserPriorityEnum,
} from "@store/user-store";
import {
  $SelectedPrescription,
  pushToPrescriptions,
  setSelectedPrescription,
  updatePrescription,
  fetchPrescription,
} from "@store/prescription-store";
import { $Departments, $DepartmentState, fetchDepartments } from "@store/company/departments-store";
import { $Employees, $EmployeesStates, fetchEmployees } from "@store/company/employees-store";
import { $Workers, $WorkersDataStates, getWorkers } from "@store/workers-store";
import { WorkerShortDataT } from "@interfaces/user";
import { CompanyContactT } from "@interfaces/company/company";
import { PrescriptionOffenseT, PrescriptionOffenseItemT, PrescriptionOffenseEmployesT, PrescriptionCreatePropsT } from "@interfaces/prescriptions";
import { WorkplanGroupT } from "@interfaces/workplan";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RichEditor } from "@ui/rich-editor";
import PrescriptionService from "@services/prescription-service";
import { TextField, MenuItem, Autocomplete, Tooltip, IconButton, Stack, Divider } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupRemoveIcon from '@mui/icons-material/GroupRemove';
import { Loader } from "@ui/indicators";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ruRU } from "@mui/x-date-pickers/locales";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import Validator from "@utils/validator";
import clsx from "clsx";
import style from "@scss/pages/prescription/create-prescription.module.scss";
import { useUnit } from "effector-react";
import useStyles from "@ui/material-ui-styles";
import { useSelectedCompanyQuery } from "@lib/company";
import { useWorkplanGroupsQuery } from "@lib/workplan";
import { useTrainingProceduresQuery } from "@lib/procedures";
import { useBriefingProceduresQuery } from "@lib/procedures/useBriefingProceduresQuery";
import { useGeneralMedicalExamControlsQuery } from "@lib/medical-examination";
import moment from "moment";

const CreatePrescription: React.FC = () => {
  const prescription = useUnit($SelectedPrescription);
  const departments = useUnit($Departments);
  const { isFetched: isDepartmentFetched } = useUnit($DepartmentState);
  const permissions = useUnit($UserAddPermissions);
  const workers = useUnit($Workers) as WorkerShortDataT[];
  const { isFetched: isFetchedWorkers } = useUnit($WorkersDataStates);
  const employees = useUnit($Employees);
  const { isFetched: isEmployeesFetched } = useUnit($EmployeesStates);

  const { company } = useSelectedCompanyQuery();
  const [validation, setValidation] = useState({
    toError: "",
    dateAcceptError: "",
    offenseError: "",
    departmentError: ""
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [groups, setGroups] = useState<WorkplanGroupT[]>([]);
  const [to, setTo] = useState<number>(0);
  const [copy, setCopy] = useState<CompanyContactT[]>([]);
  const [offense, setOffense] = useState<PrescriptionOffenseT[]>([]);
  const [comment, setComment] = useState<string>(prescription ? prescription.comment : '');
  const [creator, setCreator] = useState<WorkerShortDataT>({ name: 'Владелец', id: 0 } as WorkerShortDataT);
  const [sendCreate, setSendCreate] = useState(false);

  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const history = useNavigate();

  const editMode = !!id

  const { data } = useWorkplanGroupsQuery({
    companies: company ? [company.id] : [],
  });

  useEffect(() => {
    if (data) setGroups(data);
    if (workers && company) {
      setCreator(workers.find(e => e.id === +company?.responsibleUser!)!);
    }
  }, [data, workers, company]);

  useEffect(() => {
    if (!isDepartmentFetched) {
      fetchDepartments();
    }
    if(!isFetchedWorkers){
      getWorkers();
    }
    if (editMode && !prescription) {
      fetchPrescription({ id: +id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmployeesFetched) fetchEmployees();
  }, [isEmployeesFetched]);

  useEffect(() => {
    if(editMode && prescription){
      const workerCreator = workers.find(e => e.name === prescription.creator);
      if(workerCreator) setCreator(workerCreator);

      setTo(+prescription.to);
      setComment(prescription.comment)
      if(prescription.copy && company?.contacts){
        setCopy(prescription.copy);
      }
      if(prescription.offense){
        setOffense(JSON.parse(prescription.offense));
      }
    }
    if(!editMode){
      setComment('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, prescription]);

  const listTrainingEmployees = employees.filter(employee => employee.position.trainingPrograms?.find(tp => tp.category?.title === 'Охрана труда'));
  const trainingParams = {
    categories: [1],
    limit: 1000,
    offset: 0,
    searchString: '',
    employees: listTrainingEmployees.map(e => e.id),
    companies: [company?.id!],
    isArchived: false,
    isConfirmed: true,
    isRequired: true,
    isShowOneTime: false,
    deadline: [-2147483648, 0]
  };
  const trainingControlsCompany = useTrainingProceduresQuery(trainingParams, true);

  const briefingParams = {
    categories: [1],
    limit: 1000,
    offset: 0,
    searchString: '',
    employees: listTrainingEmployees.map(e => e.id),
    companies: [company?.id!],
    isArchived: false,
    isConfirmed: true,
    isRequired: true,
    isShowOneTime: false,
    deadline: [-2147483648, 0]
  };
  const briefingControlsCompany = useBriefingProceduresQuery(briefingParams, true);

  const medicalParams = {
    categories: [1, 2, 3, 4, 5, 6],
    limit: 1000,
    offset: 0,
    searchString: '',
    // employees: listTrainingEmployees.map(e => e.id),
    companies: [company?.id!],
    isArchived: false,
    isConfirmed: true,
    isRequired: true,
    isShowOneTime: false,
    deadline: [-2147483648, 0]
  };
  const medicalControlsCompany = useGeneralMedicalExamControlsQuery(medicalParams);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!company) return;

    const to = document.querySelector('input[name="to"]') as HTMLInputElement;
    const date_accept = document.querySelector('input[name="date_accept"]') as HTMLInputElement;
    const department = document.querySelector('input[name="department"]') as HTMLInputElement;

    const validating: any = {
      ...Validator(to.value, "to")
        .isRequired("Это поле обязательно")
        .getErrors(),
      ...Validator(date_accept.value, "dateAccept")
        .isRequired("Это поле обязательно")
        .getErrors(),
      ...Validator(department.value, "department")
        .isRequired("Необходимо выбрать подразделение")
        .getErrors(),
      ...Validator(offense.length > 0 ? "true" : "", "offense")
        .isRequired("Необходимо добавить хотя бы одно нарушение")
        .getErrors(),
    };

    if (Validator.hasError(validating)) {
      return setValidation(validating);
    }

    const data = {
      to: to ? company?.contacts.find(e => e.id === +to.value)! : '',
      copy: copy,
      date_accept: date_accept.value,
      offense: offense,
      companyId: company.id,
      department: +department.value,
      comment: comment,
      send: sendCreate,
    } as PrescriptionCreatePropsT;
    if(permissions.hasRequiredLevel(UserPriorityEnum.Owner) && creator && creator.name && creator.name.length > 0){
      data.creator = creator.name;
    }

    if (editMode) {
      if (!prescription) return;
      return PrescriptionService.update(
        prescription.id,
        data,
        (err, res) => {
          if (err || !res) {
            if (err?.response?.status === 422) {
              return setValidation(validating);
            }
            return console.error("При обновлении предписания произошла ошибка");
          }
          updatePrescription(res.data);
          setSelectedPrescription(res.data);
          history("/company/prescriptions");
        }
      );
    }
    // CREATING NEW PRESCRIPTION
    PrescriptionService.create(data, async (err, res) => {
      if (err || !res) {
        if (err?.response?.status === 422) {
          return setValidation(validating);
        }
        return console.error("При создании предписания произошла ошибка");
      }
      pushToPrescriptions([res.data]);
      setSelectedPrescription(res.data);
      history("/company/prescriptions");
    });
  };

  const handleChangeTo = (to: number) => {
    setTo(to);
    setCopy([]);
  }

  const addOffense = (e: any) => {
    const newOffense = {} as PrescriptionOffenseT;
    newOffense.category = { title: '' } as WorkplanGroupT;
    newOffense.items = [{} as PrescriptionOffenseItemT];
    newOffense.items[0].employes = [{ name: '' } as PrescriptionOffenseEmployesT];
    newOffense.items[0].content = '';
    offense.push(newOffense);
    return setOffense([...offense]);
  };

  const addOffenseItem = (index: number) => {
    const newItem = {} as PrescriptionOffenseItemT;
    newItem.content = '';
    newItem.employes = [{ name: '' } as PrescriptionOffenseEmployesT];
    offense[index].items.push(newItem)
    return setOffense([...offense]);
  }

  const addOffenseItemEmployee = (index: number, item: number) => {
    if(offense[index].items[item].employes.length < 5){
      offense[index].items[item].employes.push({ name: '' } as PrescriptionOffenseEmployesT);

      return setOffense([...offense]);
    }
  }

  const removeOffense = (index: number) => {
    const newOffense = [...offense];
    newOffense.splice(index, 1);
    return setOffense(newOffense);
  };

  const removeOffenseItem = (index: number, item: number) => {
    offense[index].items.splice(item, 1);
    return setOffense([...offense]);
  }

  const removeOffenseItemEmployee = (index: number, item: number, employee: number) => {
    if(offense[index].items[item].employes.length > 1){
      offense[index].items[item].employes.splice(employee, 1);
      return setOffense([...offense]);
    }
  }

  const changeOffense = (index: number, type: 'category' | 'employes' | 'position', value: string | PrescriptionOffenseItemT | PrescriptionOffenseEmployesT | WorkplanGroupT, item?: number, employee?: number) => {
    switch(type){
      case "category":
        if(typeof value !== 'string' && 'title' in value){
          offense[index].category = value;
        }
        if(!offense[index].items){
          offense[index].items = [{} as PrescriptionOffenseItemT];
        }
        break;
      case "employes":
        if(item !== undefined && employee !== undefined){
          if(typeof value === 'string'){
            offense[index].items[item].employes[employee].name = value;
          }else if('name' in value){
            offense[index].items[item].employes[employee].name = value.name;
            offense[index].items[item].employes[employee].position = value.position;
          }
        }
        break;
      case "position":
        if(item !== undefined && employee !== undefined && typeof value === 'string'){
          offense[index].items[item].employes[employee].position = value;
        }
        break;
    }
    return setOffense([...offense]);
  };

  const handleOffenseContent = (index: number, itemIndex: number, content: string) => {
    offense[index].items[itemIndex].content = content;

    setOffense([...offense]);
  }

  const handleGenerateList = async (index: number, item: number, type: 'training' | 'briefings' | 'medical') => {
    switch(type){
      case "training":
        const responseTraining = await trainingControlsCompany.refetch();
        if(responseTraining.data && responseTraining.data.length && responseTraining.data[0].body && responseTraining.data[0].body.length){
          offense[index].items[item].content = `
            ${offense[index].items[item].content}
            <table>
              <thead>
                <tr><th>Должность</th><th>ФИО</th><th>Программа обучения</th></tr>
              </thead>
              <tbody>
              ${responseTraining.data[0].body.map(e => (
                `<tr><td>${e.positionTitle}</td><td>${e.employeeName}</td><td>${e.programTitle}</td></tr>`
              ))}
              </tbody>
            </table>
          `;
          setTimeout(() => {
            const regex = new RegExp(`<p\\b[^>]*>([,\\s]{${responseTraining.data[0].body.length - 2},${responseTraining.data[0].body.length + 2}})</p>`, "gim");
            offense[index].items[item].content = offense[index].items[item].content.replace(regex, "");
            return setOffense([...offense]);
          }, 1)
          return setOffense([...offense]);
        }
        return;
      case "briefings":
        const responseBriefings = await briefingControlsCompany.refetch();
        if(responseBriefings.data && responseBriefings.data.length && responseBriefings.data[0].body && responseBriefings.data[0].body.length){
          offense[index].items[item].content = `
            ${offense[index].items[item].content}
            <table>
              <thead>
                <tr><th>Должность</th><th>ФИО</th><th>Инструктаж</th></tr>
              </thead>
              <tbody>
              ${responseBriefings.data[0].body.map(e => (
                `<tr><td>${e.positionTitle}</td><td>${e.employeeName}</td><td>${e.programTitle}</td></tr>`
              ))}
              </tbody>
            </table>
          `;
          setTimeout(() => {
            const regex = new RegExp(`<p\\b[^>]*>([,\\s]{${responseBriefings.data[0].body.length - 2},${responseBriefings.data[0].body.length + 2}})</p>`, "gim");
            offense[index].items[item].content = offense[index].items[item].content.replace(regex, "");
            return setOffense([...offense]);
          }, 1)
          return setOffense([...offense]);
        }
        return;
      case "medical":
        const responseMedicals = await medicalControlsCompany.refetch()
        if(responseMedicals.data && responseMedicals.data.length && responseMedicals.data){
          offense[index].items[item].content = `
            ${offense[index].items[item].content}
            <table>
              <thead>
                <tr><th>Должность</th><th>ФИО</th><th>Инструктаж</th></tr>
              </thead>
              <tbody>
              ${responseMedicals.data.map(e => (
                e.body.length > 0 ? `
                <tr><td colspan="3"><b>${e.title}</b></td></tr>
                ${e.body.map(d => (
                  `<tr><td>${d.positionTitle}</td><td>${d.employeeName}</td><td>${d.factorName}</td></tr>`
                ))}
                ` : ''
              ))}
              </tbody>
            </table>
          `;
          setTimeout(() => {
            const regex = new RegExp(`<p\\b[^>]*>([,\\s]{${responseMedicals.data.map(d => d.body).flat().length - responseMedicals.data.length - 1},${responseMedicals.data.map(d => d.body).flat().length + responseMedicals.data.length + 1}})</p>`, "gim");
            offense[index].items[item].content = offense[index].items[item].content.replace(regex, "");
            return setOffense([...offense]);
          }, 1)
          return setOffense([...offense]);
        }
        return;
    }
  }

  if(editMode && !prescription) return <Loader />;

  return (
    <div className={clsx(style.creation_content_container)}>
      <div className={clsx(style.title_panel)}>
        {!editMode ? "Создать предписание" : "Изменить предписание"}
      </div>
      <div className="underline" />
      <form className={`${classes.root}`} onSubmit={handleSubmit}>
        {permissions.hasRequiredLevel(UserPriorityEnum.Owner) && (
          <Autocomplete
            freeSolo
            fullWidth
            options={workers}
            getOptionLabel={(option) => (typeof option !== 'string' ? option.name : option)}
            getOptionKey={(option) => (typeof option !== 'string' ? option.id : option)}
            value={creator}
            isOptionEqualToValue={(option, value) => option.id === creator?.id}
            onChange={(e, value) => {
              if (value && typeof value !== 'string') setCreator(value);
            }}
            onKeyUp={(event) => {
              if (event.key === 'Backspace') {
                setCreator({ name: '' } as WorkerShortDataT);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Отправитель"
                placeholder="Введите значение"
                name="creator"
              />
            )}
          />
        )}
        <TextField
          select
          label="Кому"
          variant="filled"
          name="to"
          value={to}
          onChange={(e) => handleChangeTo(+e.target.value)}
          required
        >
          {company?.contacts.map((item) => (
            <MenuItem value={item.id} key={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
        <p className="error-text">{validation.toError}</p>
        {company?.contacts && company?.contacts.length > 1 && (
          <Autocomplete
            fullWidth
            multiple
            limitTags={5}
            options={to !== 0 ? company?.contacts.filter(e => e.id !== to) : company?.contacts}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id!}
            value={copy}
            onChange={(e, value) => setCopy(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                label="Копии"
                placeholder="Выберите значение"
              />
            )}
          />
        )}
        <TextField
          select
          label="Подразделение"
          variant="filled"
          name="department"
          defaultValue={editMode ? prescription?.department : ''}
          required
        >
          {departments.map((item) => (
            <MenuItem value={item.id} key={`department_${item.id}`}>
              {item.title}
            </MenuItem>
          ))}
        </TextField>
        <p className="error-text">{validation.departmentError}</p>
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale={"ru"}
          localeText={
            ruRU.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <DatePicker
            defaultValue={editMode && prescription ? moment(prescription.date_accept, 'DD.MM.YYYY') : null}
            open={isCalendarOpen}
            onOpen={() => setIsCalendarOpen(true)}
            onClose={() => setIsCalendarOpen(false)}
            format="DD.MM.YYYY"
            name="date_accept"
            slotProps={{
              textField: {
                label: "Дата о решении выполнения",
                variant: "filled",
                error: validation.dateAcceptError.length > 0,
                placeholder: "Выберите дату",
                onClick: () => setIsCalendarOpen(true),
              },
            }}
          />
        </LocalizationProvider>
        <p className="error-text">{validation.dateAcceptError}</p>
        <Stack sx={{ mt: 2 }}>
        {(editMode && prescription) ? (
          <RichEditor
            inline
            minHeight={200}
            key="editor_comment"
            label="Комментарий"
            initialContent={prescription.comment}
            handleContent={(content: string) => setComment(content)}
          />
        ) : (
          <RichEditor
            inline
            minHeight={200}
            key="editor_comment"
            label="Комментарий"
            initialContent={comment}
            handleContent={(content: string) => setComment(content)}
          />
        )}
        </Stack>
        <div className={clsx(style.offense)}>
          <p className={clsx(style.offense_title)}>
            Нарушения:
            <Tooltip title="Добавить" placement="top" arrow>
              <IconButton
                aria-label="fingerprint"
                color="success"
                onClick={addOffense}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          </p>
          {offense && offense.map((e, i) => (
            <Stack direction="column" spacing={2} mt={2} width={1} sx={i !== offense.length - 1 ? {  borderBottom: '1px dashed grey' } : {}} pb={1}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Autocomplete
                  fullWidth
                  size="small"
                  options={groups}
                  getOptionLabel={(option) => option.title}
                  getOptionKey={(option) => option.id!}
                  noOptionsText="Категория не найдена"
                  value={e.category}
                  onChange={(e, value) => {
                    if (value){
                      changeOffense(i, 'category', value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      label="Категория нарушения"
                      placeholder="Введите значение"
                      sx={{ marginTop: '0 !important' }}
                    />
                  )}
                />
                <Stack direction="row" pt={0.5}>
                  <Tooltip title="Удалить" placement="top" arrow>
                    <IconButton
                      aria-label="fingerprint"
                      color="error"
                      sx={{ maxHeight: '40px' }}
                      onClick={() => removeOffense(i)}
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              <Stack direction="column" spacing={2} sx={{ pl: 2 }}>
              {e.items && e.items.map((item: PrescriptionOffenseItemT, t: number) => (
                <Stack direction="row" spacing={1} className={clsx(style.item)} key={`${i}_${t}`}>
                  <Stack sx={{ minWidth: '60%' }}>
                    <RichEditor
                      inline
                      minHeight={200}
                      key={`${i}_${t}_editor`}
                      label="Описание нарушения"
                      btn={
                        e.category && e.category.title.toLowerCase().indexOf('обучение') !== -1 ? (
                          <button className="btn_small" type="button" onClick={() => handleGenerateList(i, t, 'training')}>Сформировать список обучений</button>
                        ) : e.category && e.category.title.toLowerCase().indexOf('инструктаж') !== -1 ? (
                          <button className="btn_small" type="button" onClick={() => handleGenerateList(i, t, 'briefings')}>Сформировать список инструктажей</button>
                        ) : e.category && e.category.title.toLowerCase().indexOf('медицинские') !== -1 ? (
                          <button className="btn_small" type="button" onClick={() => handleGenerateList(i, t, 'medical')}>Сформировать список мед. осмотров</button>
                        ) : undefined
                      }
                      initialContent={item.content}
                      handleContent={(content: string) => handleOffenseContent(i, t, content)}
                    />
                  </Stack>
                  <Stack direction="column" sx={{ width: '100%' }}>
                    <Stack direction="row" justifyContent="end" sx={{ marginTop: "-7px !important" }}>
                      <Tooltip title="Добавить ответственного" placement="top" arrow>
                        <IconButton
                          aria-label="fingerprint"
                          color="success"
                          sx={{ maxHeight: '40px' }}
                          onClick={() => addOffenseItemEmployee(i, t)}
                        >
                          <GroupAddIcon />
                        </IconButton>
                      </Tooltip>
                      <Divider orientation="vertical" sx={{ borderStyle: 'dashed', height: '50%', mt: 1.2, mx: 1 }} />
                      <Tooltip title="Удалить описание нарушения" placement="top" arrow>
                        <IconButton
                          aria-label="fingerprint"
                          color="error"
                          sx={{ maxHeight: '40px' }}
                          onClick={() => removeOffenseItem(i, t)}
                        >
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Добавить описание нарушения" placement="top" arrow>
                        <IconButton
                          aria-label="fingerprint"
                          color="success"
                          sx={{ maxHeight: '40px' }}
                          onClick={() => addOffenseItem(i)}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Stack direction="column" gap={1}>
                    {item.employes && item.employes.map((employee, eIndex) => (
                      <Stack direction="row" spacing={1} alignItems="center" key={`${i}_${t}_${eIndex}`} sx={eIndex !== item.employes.length - 1 ? {  borderBottom: '1px dashed #d0d5db' } : {}} pb={1}>
                        <Autocomplete
                          fullWidth
                          freeSolo
                          autoSelect
                          size="small"
                          options={employees ? employees.sort((a, b) => (a.position.department.id - b.position.department.id)) : []}
                          getOptionLabel={(option) => (typeof option !== 'string' ? option.name : option)}
                          getOptionKey={(option) => (typeof option !== 'string' ? option.id : option)}
                          groupBy={(option) => option.position.department.title}
                          inputValue={employee.name}
                          value={employee.name}
                          onChange={(e, value) => {
                            if (value){
                              changeOffense(i, 'employes', typeof value !== 'string' ? { name: value.name, position: value.position.title } : value, t, eIndex);
                            }
                          }}
                          onInputChange={(e, value) => changeOffense(i, 'employes', value, t, eIndex)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              variant="filled"
                              label="Сотрудник"
                              placeholder="Введите значение"
                              sx={{ marginTop: '0 !important' }}
                            />
                          )}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Должность"
                          variant="filled"
                          value={employee.position}
                          sx={{ marginTop: '0 !important' }}
                          onChange={(e) => changeOffense(i, 'position', e.target.value, t, eIndex)}
                        />
                        <Tooltip title="Удалить ответственного" placement="top" arrow>
                          <IconButton
                            aria-label="fingerprint"
                            color="error"
                            sx={{ maxHeight: '40px' }}
                            onClick={() => removeOffenseItemEmployee(i, t, eIndex)}
                          >
                            <GroupRemoveIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                    </Stack>
                  </Stack>
                </Stack>
              ))}
              </Stack>
            </Stack>
          ))}
        </div>
        <p className="error-text">{validation.offenseError}</p>
        <button onClick={() => setSendCreate(false)} className={clsx(style.submit_btn)} type="submit">
          {editMode ? 'Сохранить' : 'Создать'}
        </button>
        <button onClick={() => setSendCreate(true)} className={clsx(style.submit_btn)} type="submit">
          {editMode ? 'Сохранить и отправить' : 'Создать и отправить'}
        </button>
      </form>
    </div>
  );
};

export default CreatePrescription;
